/**
 * The plan page's Steps and Files to change, read back into their parts.
 *
 * The backend writes both sections from structured data
 * (`workItems/planPage.ts`: `renderStepsSection`, `renderFilesToChangeSection`)
 * and the reader wants that structure back to lay them out: a step's title as
 * the line you scan, its detail and cases folded under it, its files as chips;
 * the files as a table. Each parse is the exact inverse of its renderer and
 * nothing more. A line it does not recognize makes it return null, and the
 * reader then prints the section's markdown as written, so an amendment or an
 * older page shape can never lose text to a parse that guessed.
 */

export interface PlanStepView {
  number: number;
  /** The bold head, or null for a step written before titles existed. */
  title: string | null;
  /** Markdown, dedented. For an untitled step, the whole step. */
  detail: string;
  cases: string[];
  files: string[];
  testFiles: string[];
}

export interface PlanFileView {
  path: string;
  /** The plan declared it a file the work creates ("(new file)"). */
  isNew: boolean;
  /** The steps whose own file lists name it. */
  steps: number[];
}

const ITEM = /^(\d+)\.\s+(.*)$/;
const TITLE = /^\*\*(.+)\*\*$/;
const INDENT = "   ";
const CASE = /^- (.*)$/;
const REFS = /^(?:Files|Tests): /;
const CODE = /`([^`]+)`/g;

function codeSpans(text: string): string[] {
  return [...text.matchAll(CODE)].map((match) => match[1]!);
}

export function parsePlanSteps(lines: readonly string[]): PlanStepView[] | null {
  const steps: PlanStepView[] = [];
  const detail: string[][] = [];
  for (const line of lines) {
    if (line.trim() === "") {
      if (steps.length > 0) detail[steps.length - 1]!.push("");
      continue;
    }
    const item = ITEM.exec(line);
    if (item) {
      const text = item[2]!.trim();
      const title = TITLE.exec(text)?.[1] ?? null;
      steps.push({ number: Number(item[1]), title, detail: "", cases: [], files: [], testFiles: [] });
      detail.push(title ? [] : [text]);
      continue;
    }
    // Anything else must be a continuation of the current step.
    if (steps.length === 0 || !line.startsWith(INDENT)) return null;
    const step = steps[steps.length - 1]!;
    const body = line.slice(INDENT.length);
    const entry = CASE.exec(body);
    if (step.title && entry) {
      step.cases.push(entry[1]!.trim());
    } else if (step.title && REFS.test(body)) {
      for (const part of body.split(" · ")) {
        if (part.startsWith("Files: ")) step.files.push(...codeSpans(part));
        else if (part.startsWith("Tests: ")) step.testFiles.push(...codeSpans(part));
      }
    } else {
      detail[steps.length - 1]!.push(body);
    }
  }
  if (steps.length === 0) return null;
  steps.forEach((step, index) => {
    step.detail = detail[index]!.join("\n").trim();
  });
  return steps;
}

const FILE_ITEM = /^- `([^`]+)`( \(new file\))?$/;

export function parsePlanFiles(
  lines: readonly string[],
  steps: readonly PlanStepView[],
): PlanFileView[] | null {
  const files: PlanFileView[] = [];
  for (const line of lines) {
    if (line.trim() === "") continue;
    const item = FILE_ITEM.exec(line.trim());
    if (!item) return null;
    const path = item[1]!;
    files.push({
      path,
      isNew: Boolean(item[2]),
      steps: steps
        .filter((step) => step.files.includes(path) || step.testFiles.includes(path))
        .map((step) => step.number),
    });
  }
  return files.length > 0 ? files : null;
}
